import { useState, useEffect } from 'react';
import { CODE_DISCOUNT_TYPES } from '@affily/shared/constants';
import { percentToBps, bpsToPercent } from '@affily/shared/money';
import { api } from '../lib/api.js';
import { useFetch } from '../hooks/useFetch.js';
import { TrackingLink } from './TrackingLink.jsx';
import { Button, Field, Input, Select, Alert, Badge, Spinner, SectionTitle } from './ui.jsx';

const DISCOUNT_LABELS = { percent: 'Percent off', fixed: 'Amount off' };

/**
 * Creator codes for one program: on/off, the shopper discount, and the list
 * of codes the brand has to create in their store.
 *
 * Codes are how podcasts, video and games drive sales — nobody clicks a link
 * from a podcast. A connected store will create the codes itself; until then
 * this list is the brand's to-do.
 */
export function CreatorCodesSettings({ programId }) {
  const { data, loading, reload } = useFetch(`/programs/${programId}/codes`);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    const { codes } = data;
    setForm({
      enabled: codes.enabled,
      discountType: codes.discountType,
      percent: codes.discountBps ? String(bpsToPercent(codes.discountBps)) : '',
      amount: codes.discountCents ? String(codes.discountCents / 100) : '',
    });
  }, [data]);

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      await api.put(`/programs/${programId}/codes`, {
        enabled: form.enabled,
        discountType: form.discountType,
        discountBps: form.discountType === 'percent' ? percentToBps(Number(form.percent || 0)) : 0,
        discountCents:
          form.discountType === 'fixed' ? Math.round(Number(form.amount || 0) * 100) : 0,
      });
      setSaved(true);
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (loading || !form) return <Spinner />;

  const toCreate = (data.partnerships ?? []).filter((p) => p.promoCode && p.sync !== 'synced');

  return (
    <section>
      <SectionTitle hint="For podcasts, video and games — where people type a code instead of clicking">
        Creator codes
      </SectionTitle>

      <form onSubmit={save} className="space-y-4 rounded-lg border border-rule bg-paper-raised p-4">
        <Alert>{error}</Alert>
        {saved && <Alert tone="positive">Saved.</Alert>}

        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            className="mt-0.5 size-4 accent-accent"
          />
          <span>
            <span className="block text-sm font-medium">Give every creator a discount code</span>
            <span className="block text-xs text-ink-muted">
              Each creator gets their own code, like RAE10. A sale using it is credited to them,
              with or without a click. You can rename any code on the Partnerships page.
            </span>
          </span>
        </label>

        {form.enabled && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Shopper discount">
              <Select
                value={form.discountType}
                onChange={(e) => setForm({ ...form, discountType: e.target.value })}
              >
                {CODE_DISCOUNT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {DISCOUNT_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
            {form.discountType === 'percent' ? (
              <Field label="Percent" constraint="0 for no discount">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.percent}
                  onChange={(e) => setForm({ ...form, percent: e.target.value })}
                />
              </Field>
            ) : (
              <Field label="Amount" constraint="In your program’s currency; 0 for no discount">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </Field>
            )}
          </div>
        )}

        <Button type="submit" disabled={busy}>
          Save
        </Button>
      </form>

      {data.codes.enabled && toCreate.length > 0 && (
        <div className="mt-4 rounded-lg border border-rule bg-paper-raised p-4">
          <p className="text-sm font-medium">Create these codes in your store</p>
          <p className="mt-0.5 text-xs text-ink-muted">
            Shoppers can only use a code your store knows about
            {data.codes.description ? `, each giving ${data.codes.description}` : ''}. Connecting
            your store will do this automatically.
          </p>
          <ul className="mt-3 space-y-2">
            {toCreate.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-2">
                <TrackingLink url={p.promoCode} noun="code" onError={setError} />
                <span className="text-xs text-ink-muted">{p.creator?.displayName}</span>
                {p.sync === 'failed' && (
                  <Badge tone="negative" title={p.syncError ?? undefined}>
                    sync failed
                  </Badge>
                )}
                {p.status === 'paused' && <Badge tone="neutral">paused</Badge>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default CreatorCodesSettings;
